import React from 'react';
import { Link } from 'react-router-dom';
import resolveImageUrl from '../utils/resolveImageUrl';

function BookingStatusBadge({ status }) {
  const label = status || 'Available';
  const isAvailable = label.toLowerCase() === 'available';

  return (
    <span
      className={`vehicle-status-pill ${
        isAvailable
          ? 'vehicle-status-pill--available'
          : 'vehicle-status-pill--limited'
      }`}
    >
      {label}
    </span>
  );
}

function VehicleCard({
  product,
  categoryName,
  role,
  viewMode = 'grid',
  onWishlist,
  hideDefaultFooter = false,
}) {
  if (!product) return null;

  const image = resolveImageUrl(product.imageURL);
  const description = product.description || '';
  const descriptionPreview =
    description.length > 95 ? `${description.substring(0, 95)}...` : description;
  const detailPath = `/vehicle/show/${product.id}/${encodeURIComponent(product.name)}`;
  const displayCategory = categoryName || product.categoryName || 'View category';
  const parsedPrice = Number(product.price);
  const displayPrice = Number.isFinite(parsedPrice)
    ? `US$${parsedPrice.toLocaleString()}/day`
    : null;
  const displayYear = product.year || product.modelYear;
  const metaItems = [
    displayYear ? { label: String(displayYear), className: 'vehicle-meta-pill--year' } : null,
    displayPrice ? { label: displayPrice, className: 'vehicle-meta-pill--price' } : null,
  ].filter(Boolean);

  if (viewMode === 'list') {
    return (
      <article className="vehicle-card vehicle-card--list overflow-hidden rounded-lg md:flex">
        <Link to={detailPath} className="vehicle-card-image-link block overflow-hidden md:w-72">
          <img
            src={image}
            alt={product.name}
            className="vehicle-card-image h-56 w-full"
          />
        </Link>
        <div className="vehicle-card-body flex flex-1 flex-col p-5">
          <Link to={detailPath} className="text-primary-theme no-underline">
            <h3 className="card-title mb-2 text-2xl">{product.name}</h3>
          </Link>
          {metaItems.length > 0 && (
            <div className="vehicle-card-meta mb-3 flex flex-wrap gap-2">
              {metaItems.map((item) => (
                <span key={item.label} className={`vehicle-meta-pill ${item.className}`}>
                  {item.label}
                </span>
              ))}
            </div>
          )}
          <BookingStatusBadge status={product.bookingStatus} />
          <p className="mt-3 flex-1 text-secondary-theme">{descriptionPreview}</p>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <Link
              to={`/category/show/${product.categoryId}`}
              className="text-accent no-underline"
            >
              {displayCategory}
            </Link>
            <div className="flex flex-wrap items-center gap-2">
              {onWishlist && (
                <button
                  type="button"
                  onClick={() => onWishlist(product.id)}
                  className="btn-luxury-outline"
                >
                  Wishlist
                </button>
              )}
              {role === 'admin' && (
                <Link to={`/admin/vehicle/${product.id}`} className="btn-luxury-outline">
                  Edit
                </Link>
              )}
              <Link to={detailPath} className="btn-luxury">
                View Details
              </Link>
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="vehicle-card flex h-full flex-col overflow-hidden rounded-lg">
      <Link to={detailPath} className="vehicle-card-image-link block overflow-hidden">
        <img
          src={image}
          alt={product.name}
          className="vehicle-card-image h-56 w-full"
        />
      </Link>
      <div className="vehicle-card-body flex flex-1 flex-col p-5">
        <Link to={detailPath} className="text-primary-theme no-underline">
          <h3 className="card-title mb-2 text-xl">{product.name}</h3>
        </Link>
        {metaItems.length > 0 && (
          <div className="vehicle-card-meta mb-3 flex flex-wrap gap-2">
            {metaItems.map((item) => (
              <span key={item.label} className={`vehicle-meta-pill ${item.className}`}>
                {item.label}
              </span>
            ))}
          </div>
        )}
        <BookingStatusBadge status={product.bookingStatus} />
        <p className="mt-3 flex-1 text-secondary-theme">{descriptionPreview}</p>
        {!hideDefaultFooter && (
          <div className="mt-4 flex items-center justify-between gap-3">
            <Link
              to={`/category/show/${product.categoryId}`}
              className="text-accent no-underline"
            >
              {displayCategory}
            </Link>
            <Link to={detailPath} className="btn-luxury">
              View
            </Link>
          </div>
        )}
      </div>
    </article>
  );
}

export default VehicleCard;
